import { useEffect, useRef, type RefObject } from "react";

/**
 * Dialog behaviour for a modal or bottom sheet: focus moves in on open, Escape closes, Tab and
 * Shift+Tab wrap inside (a focus trap, with a focusin backstop), and focus returns to where it
 * came from on close. Attach the returned ref to the dialog, `initialFocus` to the control that
 * should be focused first, and `returnFocus` to whatever should get focus back (it must still
 * exist and be enabled then).
 */
export function useDialog(
  open: boolean,
  onClose: () => void,
  initialFocus: RefObject<HTMLElement>,
  returnFocus: RefObject<HTMLElement>,
) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    if (!open) return;
    initialFocus.current?.focus();

    function focusable(): HTMLElement[] {
      if (!dialogRef.current) return [];
      return Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("disabled"));
    }

    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        closeRef.current();
        return;
      }
      if (e.key !== "Tab") return;
      const els = focusable();
      if (els.length === 0) return;
      const first = els[0];
      const last = els[els.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }

    function onFocusIn(e: FocusEvent) {
      if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
        initialFocus.current?.focus();
      }
    }

    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("focusin", onFocusIn);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("focusin", onFocusIn);
      returnFocus.current?.focus();
    };
  }, [open, initialFocus, returnFocus]);

  return dialogRef;
}
